import { useEffect, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { api } from '../api/client'
import type { DropView } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { PageShell } from '../components/BrandChrome'
import { Alert, Button, Card, Subtitle, Title, buttonClassName } from '../components/ui'
import { errorMessage, formatBytes, formatWhen } from '../lib/format'
import { formatPhoneHint } from '../lib/phone'

export function DropsPage() {
  const { user, loading, logout } = useAuth()
  const [drops, setDrops] = useState<DropView[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!user?.mfa_enrolled) return
    let cancelled = false
    ;(async () => {
      try {
        const res = await api.listDrops()
        if (!cancelled) setDrops(res.drops)
      } catch (err) {
        if (!cancelled) setError(errorMessage(err))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [user])

  if (loading) {
    return (
      <PageShell>
        <p className="text-sm text-slate-500">Loading…</p>
      </PageShell>
    )
  }
  if (!user) return <Navigate to="/login" replace />
  if (!user.mfa_enrolled) return <Navigate to="/mfa/setup" replace />

  return (
    <PageShell homeTo="/drops">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <Title>Your drops</Title>
          <Subtitle>Signed in as {user.email}</Subtitle>
        </div>
        <div className="flex gap-2">
          <Link to="/drops/new" className={buttonClassName('primary')}>
            New drop
          </Link>
          <Button type="button" variant="secondary" onClick={() => void logout()}>
            Sign out
          </Button>
        </div>
      </div>

      {error ? <Alert className="mb-4">{error}</Alert> : null}

      {!drops ? (
        <p className="text-sm text-slate-500">Loading drops…</p>
      ) : drops.length === 0 ? (
        <Card>
          <p className="text-slate-600">No drops yet.</p>
          <Link to="/drops/new" className={`mt-3 ${buttonClassName('primary')}`}>
            Create your first drop
          </Link>
        </Card>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Recipient</th>
                <th className="px-3 py-2">Files</th>
                <th className="px-3 py-2">Created</th>
                <th className="px-3 py-2">Expires</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {drops.map((d) => (
                <tr key={d.drop_id} className="border-b last:border-0">
                  <td className="px-3 py-2 font-medium capitalize">{d.status}</td>
                  <td className="px-3 py-2">{formatPhoneHint(d.phone_last4)}</td>
                  <td className="px-3 py-2">
                    {d.object_count} · {formatBytes(d.total_bytes)}
                  </td>
                  <td className="px-3 py-2">{formatWhen(d.created_at)}</td>
                  <td className="px-3 py-2">{formatWhen(d.expires_at)}</td>
                  <td className="px-3 py-2 text-right">
                    <Link className="text-[var(--brand-navy)] underline" to={`/drops/${d.drop_id}`}>
                      Open
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PageShell>
  )
}
